import { zodResolver } from "@hookform/resolvers/zod"
import * as React from "react"
import { useForm } from "react-hook-form"
import { useNavigate, useSearchParams } from "react-router-dom"
import { z } from "zod"
import { authApi } from "@/api/auth"
import { ApiError } from "@/api/client"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

const schema = z
  .object({
    password: z.string().min(8, "Şifre en az 8 karakter."),
    confirm: z.string(),
  })
  .refine((d) => d.password === d.confirm, { message: "Şifreler eşleşmiyor.", path: ["confirm"] })

type FormValues = z.infer<typeof schema>

export function ResetPasswordPage() {
  const [error, setError] = React.useState<string | null>(null)
  const [done, setDone] = React.useState(false)
  const nav = useNavigate()
  const [params] = useSearchParams()
  const token = params.get("token") ?? ""

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { password: "", confirm: "" },
  })

  async function onSubmit(values: FormValues) {
    setError(null)
    if (!token) {
      setError("Sıfırlama bağlantısı geçersiz veya süresi dolmuş.")
      return
    }
    try {
      await authApi.resetPassword({ token, password: values.password })
      setDone(true)
      setTimeout(() => nav("/login", { replace: true }), 1500)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Şifre güncellenemedi.")
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Yeni şifre belirle</CardTitle>
        <CardDescription>Yeni şifreni gir ve devam et.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {done && (
            <Alert>
              <AlertDescription>Şifren güncellendi. Yönlendiriliyorsun…</AlertDescription>
            </Alert>
          )}
          <div className="space-y-1.5">
            <Label>Yeni şifre</Label>
            <Input type="password" autoComplete="new-password" {...form.register("password")} />
            {form.formState.errors.password && (
              <p className="text-xs text-destructive">{form.formState.errors.password.message}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Şifre tekrar</Label>
            <Input type="password" autoComplete="new-password" {...form.register("confirm")} />
            {form.formState.errors.confirm && (
              <p className="text-xs text-destructive">{form.formState.errors.confirm.message}</p>
            )}
          </div>
          <Button type="submit" className="w-full" disabled={form.formState.isSubmitting || done}>
            Şifreyi güncelle
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
